import React from 'react';
import { motion } from 'framer-motion';
import {MapPin, Train, DollarSign, Clock, Utensils} from 'lucide-react';
import { useScrollAnimation, useParallax } from '../../hooks/useScrollAnimation';

export default function Osaka() {
  const scrollY = useParallax();
  const { ref: infoRef, isVisible: infoVisible } = useScrollAnimation(0.2);
  const { ref: areasRef, isVisible: areasVisible } = useScrollAnimation(0.2);
  const { ref: attractionsRef, isVisible: attractionsVisible } = useScrollAnimation(0.2);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero with Parallax */}
      <div className="relative h-[500px] overflow-hidden">
        <motion.img
          src="https://images.pexels.com/photos/4151484/pexels-photo-4151484.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Osaka"
          className="w-full h-full object-cover"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/90 via-[#0A2540]/70 to-transparent" />
        <motion.div 
          className="absolute inset-0 flex items-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h1 
              className="text-6xl font-bold text-white mb-3"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Osaka
            </motion.h1>
            <motion.p 
              className="text-2xl text-white/90"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Japan's Kitchen – Food, Fun & Friendly Vibes
            </motion.p>
          </div>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Info Cards with Stagger Animation */}
        <motion.div 
          ref={infoRef}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={infoVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {[
            { icon: Clock, color: 'text-[#4A90E2]', label: 'Recommended Stay', value: '2-3 days' },
            { icon: DollarSign, color: 'text-[#F7B32B]', label: 'Daily Budget', value: '¥6,000-12,000' },
            { icon: Train, color: 'text-[#FF6B35]', label: 'Best Transport', value: 'Osaka Metro' },
            { icon: Utensils, color: 'text-[#0A2540]', label: 'Signature Dish', value: 'Takoyaki, Okonomiyaki' }
          ].map((item, idx) => (
            <motion.div 
              key={idx}
              className="bg-white rounded-lg p-4 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              initial={{ opacity: 0, y: 20 }}
              animate={infoVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <item.icon className={`w-6 h-6 ${item.color} mb-2`} />
              <p className="text-sm text-gray-500">{item.label}</p>
              <p className="font-semibold text-gray-800">{item.value}</p>
            </motion.div>
          ))}
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          <motion.div 
            className="lg:col-span-2"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">About Osaka</h2>
            <p className="text-gray-600 mb-4">
              Known as "Japan's Kitchen," Osaka is a paradise for food lovers with its street food culture, vibrant nightlife, and warm, down-to-earth locals. The city's motto "kuidaore" means "eat until you drop" – and visitors take it seriously!
            </p>
            <p className="text-gray-600 mb-8">
              Beyond food, Osaka offers historic Osaka Castle, bustling shopping districts, and easy access to Universal Studios Japan and nearby Kyoto.
            </p>

            {/* Areas Section with Scroll Animation */}
            <h3 className="text-2xl font-bold text-[#0A2540] mb-4">Must-Visit Areas</h3>
            <motion.div 
              ref={areasRef}
              className="space-y-4 mb-8"
            >
              {[
                { name: 'Dotonbori', desc: 'Neon-lit food district, famous street food' },
                { name: 'Namba', desc: 'Shopping, entertainment, nightlife hub' },
                { name: 'Umeda', desc: 'Modern district, rooftop gardens, shopping' },
                { name: 'Shinsekai', desc: 'Retro neighborhood, Tsutenkaku Tower' }
              ].map((area, idx) => (
                <motion.div 
                  key={idx} 
                  className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-[#FF6B35] hover:shadow-md transition-all duration-300"
                  initial={{ opacity: 0, x: -30 }}
                  animate={areasVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  whileHover={{ x: 10 }}
                >
                  <h4 className="font-semibold text-gray-800 mb-1">{area.name}</h4>
                  <p className="text-sm text-gray-600">{area.desc}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* Attractions Section */}
            <h3 className="text-2xl font-bold text-[#0A2540] mb-4">Top Attractions</h3>
            <motion.ul 
              ref={attractionsRef}
              className="space-y-2 text-gray-700"
            >
              {[
                { name: 'Osaka Castle', desc: 'Historic landmark with museum and park' },
                { name: 'Universal Studios Japan', desc: 'World-class theme park' },
                { name: 'Dotonbori District', desc: 'Iconic neon signs and street food paradise' },
                { name: 'Osaka Aquarium Kaiyukan', desc: 'One of the world\'s largest aquariums' },
                { name: 'Sumiyoshi Taisha', desc: 'Ancient shrine with unique architecture' }
              ].map((attraction, idx) => (
                <motion.li 
                  key={idx}
                  className="flex items-start hover:bg-white hover:shadow-sm p-2 rounded transition-all duration-200"
                  initial={{ opacity: 0, x: -20 }}
                  animate={attractionsVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                >
                  <MapPin className="w-5 h-5 text-[#4A90E2] mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>{attraction.name}:</strong> {attraction.desc}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="bg-gradient-to-br from-[#FF6B35] to-[#F7B32B] rounded-xl p-6 text-white shadow-lg sticky top-4">
              <h3 className="text-2xl font-bold mb-4">Travel Tips</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start">
                  <span className="mr-2">🎫</span>
                  <span>Get an ICOCA card for easy transport payments</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🍜</span>
                  <span>Try takoyaki from street vendors in Dotonbori</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🏯</span>
                  <span>Visit Osaka Castle early to avoid crowds</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🎢</span>
                  <span>Book Universal Studios tickets online in advance</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🌙</span>
                  <span>Experience nightlife in Namba and Umeda</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}